import React from 'react'
import { library } from '@fortawesome/fontawesome-svg-core'
import { fas } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

library.add(fas)

const formatEuros = cents => {
  const value = (cents || 0) / 100
  return value.toLocaleString(I18n.locale || 'fr', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })
}

const formatDate = epoch => {
  if (!epoch) return ''
  const d = new Date(epoch * 1000)
  return d.toLocaleDateString(I18n.locale || 'fr')
}

const KPI_BG = {
  pending:  'bg-amber-50 border-amber-200',
  approved: 'bg-emerald-50 border-emerald-200',
  rejected: 'bg-red-50 border-red-200'
}

const ICON_COLOR = {
  pending:  'text-amber-600',
  approved: 'text-emerald-700',
  rejected: 'text-red-700',
  info:     'text-blue-600',
  warning:  'text-amber-600'
}

const Kpi = ({ label, value, modifier, icon }) => {
  const cardClass = modifier ? KPI_BG[modifier] || '' : 'bg-white border-gray-200'
  const iconClass = modifier ? ICON_COLOR[modifier] || 'text-gray-400' : 'text-gray-400'
  return (
    <div className={`border rounded-lg py-2 px-3.5 flex justify-between items-center ${cardClass}`}>
      <div>
        <div className='text-[11px] text-gray-500 uppercase tracking-wider'>{label}</div>
        <div className='text-[22px] font-bold text-gray-900 leading-tight'>{value}</div>
      </div>
      {icon && <FontAwesomeIcon icon={icon} size='lg' className={iconClass} />}
    </div>
  )
}

const Row = ({ record, accessory }) => {
  const showAmount = record.objectType !== 'workorder'
  return (
    <a
      href={record.path}
      target='_blank'
      rel='noopener noreferrer'
      className='grid items-center gap-3 px-1 py-2.5 border-b border-gray-100 text-[13px] last:border-b-0'
      style={{ color: 'inherit', textDecoration: 'none', gridTemplateColumns: '130px 1fr auto' }}
    >
      <div className='min-w-0'>
        {record.typeLabel && (
          <div className='text-gray-500 text-[10px] uppercase tracking-wider overflow-hidden text-ellipsis whitespace-nowrap'>{record.typeLabel}</div>
        )}
        <div className='text-gray-700 font-mono text-xs overflow-hidden text-ellipsis whitespace-nowrap'>{record.identifier}</div>
      </div>
      <div className='min-w-0'>
        <div className='font-semibold'>{record.name}</div>
        {record.ownerName && <div className='text-gray-500 text-xs'>{`${I18n.t('approvals_dashboard.by')} ${record.ownerName}`}</div>}
        {record.currentStepApprovers && record.currentStepApprovers.length > 0 && (
          <div className='text-gray-500 text-xs'>{`${I18n.t('approvals_dashboard.step_label')}: ${record.currentStepApprovers.join(', ')}`}</div>
        )}
        {accessory}
      </div>
      {showAmount
        ? <div className='font-bold text-right'>{`€${formatEuros(record.amountCents)}`}</div>
        : <div className='font-bold text-right'></div>
      }
    </a>
  )
}

const Card = ({ title, icon, iconModifier, count, children, modifier }) => {
  const full          = modifier && modifier.split(' ').includes('full')
  const needsAttn     = modifier && modifier.split(' ').includes('needs-attention')
  const cardBg        = needsAttn ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'
  const colSpanStyle  = full ? { gridColumn: '1 / -1' } : undefined
  const iconClass     = iconModifier ? ICON_COLOR[iconModifier] || 'text-gray-500' : 'text-gray-500'
  return (
    <div className={`border rounded-lg px-4.5 py-4 flex flex-col ${cardBg}`} style={{ height: '320px', padding: '16px 18px', ...colSpanStyle }}>
      <div className='flex items-center gap-2.5 mb-2.5 shrink-0'>
        <FontAwesomeIcon icon={icon} className={iconClass} />
        <h5 className='m-0 font-bold flex-1'>{title}</h5>
        {count != null && count > 0 && (
          <span className='bg-blue-500 text-white rounded-[10px] px-2 py-0.5 text-xs font-semibold'>{count}</span>
        )}
      </div>
      <div className='overflow-y-auto flex-1'>{children}</div>
    </div>
  )
}

const Empty = ({ icon, title, hint }) => (
  <div className='flex-1 flex flex-col items-center justify-center text-gray-400 text-center h-full'>
    <FontAwesomeIcon icon={icon} size='2x' />
    <p className='mt-2 mb-1'>{title}</p>
    {hint && <small>{hint}</small>}
  </div>
)

const Approvals = ({
  totals,
  awaitingMyApproval,
  myPendingOrders,
  recentlyApproved,
  recentlyRejected,
  needsAttention
}) => (
  <div className='py-6 px-8'>
    <div className='grid gap-3 mb-4' style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
      <Kpi label={I18n.t('approvals_dashboard.kpi.total')}    value={totals.total}    icon='file-alt' />
      <Kpi label={I18n.t('approvals_dashboard.kpi.pending')}  value={totals.pending}  icon='clock'         modifier='pending'  />
      <Kpi label={I18n.t('approvals_dashboard.kpi.approved')} value={totals.approved} icon='check-circle'  modifier='approved' />
      <Kpi label={I18n.t('approvals_dashboard.kpi.rejected')} value={totals.rejected} icon='times-circle'  modifier='rejected' />
    </div>

    <div className='grid gap-3 mb-3' style={{ gridTemplateColumns: '1fr 1fr' }}>
      <Card title={I18n.t('approvals_dashboard.awaiting_my_approval')} icon='clock' iconModifier='pending' count={awaitingMyApproval.length}>
        {awaitingMyApproval.length === 0 ? (
          <Empty icon='inbox' title={I18n.t('approvals_dashboard.empty.awaiting')} />
        ) : awaitingMyApproval.map(r => <Row key={`${r.objectType}-${r.id}`} record={r} />)}
      </Card>
      <Card title={I18n.t('approvals_dashboard.my_pending_orders')} icon='file-alt' iconModifier='info' count={myPendingOrders.length}>
        {myPendingOrders.length === 0 ? (
          <Empty icon='file-alt' title={I18n.t('approvals_dashboard.empty.my_pending.title')} hint={I18n.t('approvals_dashboard.empty.my_pending.hint')} />
        ) : myPendingOrders.map(r => (
          <Row
            key       = {`${r.objectType}-${r.id}`}
            record    = {r}
            accessory = {r.status === 'commented' && (
              <div className='text-blue-600 text-xs font-semibold'>{I18n.t('validation.workflow.status_badge.commented')}</div>
            )}
          />
        ))}
      </Card>
    </div>

    <div className='grid gap-3 mb-3' style={{ gridTemplateColumns: '1fr 1fr' }}>
      <Card title={I18n.t('approvals_dashboard.recently_approved')} icon='check-circle' iconModifier='approved'>
        {recentlyApproved.length === 0 ? (
          <Empty icon='check-circle' title={I18n.t('approvals_dashboard.empty.recently_approved')} />
        ) : recentlyApproved.map(r => <Row key={`${r.objectType}-${r.id}`} record={r} />)}
      </Card>
      <Card title={I18n.t('approvals_dashboard.recently_rejected')} icon='times-circle' iconModifier='rejected'>
        {recentlyRejected.length === 0 ? (
          <Empty icon='times-circle' title={I18n.t('approvals_dashboard.empty.recently_rejected')} />
        ) : recentlyRejected.map(r => <Row key={`${r.objectType}-${r.id}`} record={r} />)}
      </Card>
    </div>

    {needsAttention.length > 0 && (
      <Card title={I18n.t('approvals_dashboard.needs_attention')} icon='bell' iconModifier='warning' modifier='needs-attention full'>
        <div className='text-muted small mb-2'>{I18n.t('approvals_dashboard.reminders_have_been_sent')}</div>
        {needsAttention.map(r => (
          <Row
            key       = {`${r.objectType}-${r.id}`}
            record    = {r}
            accessory = {
              <div className='text-gray-500 text-xs'>
                {I18n.t('approvals_dashboard.reminder_sent_on', { date: formatDate(r.reminderSentAtI) })}
              </div>
            }
          />
        ))}
      </Card>
    )}
  </div>
)

export default Approvals
